import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Send, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = `/api`;

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Message Sent!",
          description: "Thanks for reaching out! We'll get back to you soon.",
        });
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      }
    } catch (error) {
      // Error handled by toast notification above
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-slate-50 to-white" style={{scrollMarginTop: '2rem'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-slate-600">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 sm:p-10 rounded-2xl shadow-xl border border-slate-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full h-12 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Your name"
                  data-testid="contact-name"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                  Company
                </label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full h-12 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Your company name"
                  data-testid="contact-company"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full h-12 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="your.email@example.com"
                  data-testid="contact-email"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                  Phone
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full h-12 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="+44 1234 567890"
                  data-testid="contact-phone"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                Message *
              </label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                className="w-full min-h-[160px] border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Tell us about your requirements..."
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-2" data-testid="contact-consent-label">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
                data-testid="contact-consent-checkbox"
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                I consent to Binary Consultancy processing my personal data to respond to this enquiry, in accordance with the{' '}
                <a href="/policies/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">
                  Privacy Policy
                </a>.
              </span>
            </label>

            <Button
              type="submit"
              disabled={isSubmitting || !consent}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            >
              {isSubmitting ? (
                'SENDING...'
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  SEND MESSAGE
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm mb-4">Or reach us directly on WhatsApp</p>
            <a
              href="https://wa.me/447483181334"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <p className="text-slate-400 text-xs mt-2">07483 181334</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;